import { useDirectories } from "../hooks/useDirectories";
import {
    Box,
    CircularProgress,
    Typography,
    IconButton,
    Dialog,
    DialogTitle,
} from "@mui/material";
import { useState } from "react";
import FolderCard from "../components/FolderCard";
import CardDocument from "../components/CardDocument";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { DirectoryType, Document } from "../../common/redux/documentsAction";
import { ContainerDocument, EmptyDataBox } from "./MyDocuments";
import RenameModal from "../components/RenameModal";
import CustomSnackbar from "../../common/components/CustomSnackbar";
import { useTranslation } from "react-i18next";
import UploadDocument from "../../profil/UploadDocument";
import CloseIcon from "@mui/icons-material/Close";
import EmptyDataDocs from "./EmptyDataDocs";
import { useDirectory } from "../hooks/useDirectory";
import DeleteModal from "../components/DeleteModal";

const MissionDocuments = () => {
    const [openUploadModal, setOpenUploadModal] = useState(false);
    const [documentToRename, setDocumentToRename] = useState<Document | null>(
        null
    );
    const [documentToDelete, setDocumentToDelete] = useState<Document | null>(
        null
    );
    const [activeDirectoryId, setActiveDirectoryId] = useState<string | null>(
        null
    );
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const { directories, loading: loadingDirectories } = useDirectories({
        type: DirectoryType.MISSION,
    });
    const {
        directory,
        loading: loadingDirectory,
        refresh: refreshDirectory,
    } = useDirectory(activeDirectoryId);
    const loading = loadingDirectories || loadingDirectory;

    const { t } = useTranslation();

    const handleCloseSnackbar = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const viewDocument = (document: Document) => {
        window.open(document.fileUri, "_blank");
    };

    const download = (doc: Document) => {
        const link = document.createElement("a");
        link.href = doc.fileUri;
        link.download = doc.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" mt={8}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box mt={2} p={1}>
            <Typography variant="h6">
                {t("manageDocuments.title.section.missiondocs")}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
                {t("manageDocuments.subtitle.section.missiondocs", {
                    count: directory?.documents.length ?? 0,
                    dirCount: directories?.length ?? 0,
                })}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
                {t("manageDocuments.description.section.missiondocs")}
            </Typography>
            {directory ? (
                <>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={2}
                        mt={4}
                    >
                        <Box display="flex" alignItems="center">
                            <IconButton
                                onClick={() => setActiveDirectoryId(null)}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                            <Typography variant="h6" ml={1}>
                                {directory.name}
                            </Typography>
                        </Box>
                    </Box>
                    <ContainerDocument>
                        {directory.documents.length === 0 ? (
                            <EmptyDataBox>
                                <EmptyDataDocs
                                    title={t(
                                        "manageDocuments.empty.mission.data.file"
                                    )}
                                    subtitle=""
                                />
                            </EmptyDataBox>
                        ) : (
                            <>
                                {directory.documents.map((document) => (
                                    <Box key={document.id}>
                                        <CardDocument
                                            document={document}
                                            onView={viewDocument}
                                            onRename={setDocumentToRename}
                                            onDelete={setDocumentToDelete}
                                            onDownload={download}
                                        />
                                    </Box>
                                ))}
                            </>
                        )}
                    </ContainerDocument>
                </>
            ) : (
                <>
                    <Box
                        display="flex"
                        flexWrap="wrap"
                        gap={4}
                        margin="1rem 0 4rem 0"
                    >
                        {directories.length === 0 ? (
                            <EmptyDataBox>
                                <EmptyDataDocs
                                    title={t(
                                        "manageDocuments.empty.mission.data.title"
                                    )}
                                    subtitle={t(
                                        "manageDocuments.empty.mission.data.subtitle"
                                    )}
                                />
                            </EmptyDataBox>
                        ) : (
                            directories.map(({ id, name }) => (
                                <FolderCard
                                    key={id}
                                    name={name}
                                    onClick={() => setActiveDirectoryId(id)}
                                />
                            ))
                        )}
                    </Box>
                </>
            )}
            <Dialog
                open={openUploadModal}
                onClose={() => setOpenUploadModal(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    <IconButton
                        aria-label="close"
                        onClick={() => setOpenUploadModal(false)}
                        sx={{ position: "absolute", right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <Box sx={{ p: 2 }}>
                    <UploadDocument
                        directoryId={directory?.id ?? null}
                        onUploadSuccess={() => {
                            refreshDirectory();
                            setOpenUploadModal(false);
                        }}
                    />
                </Box>
            </Dialog>
            <DeleteModal
                open={documentToDelete !== null}
                document={documentToDelete}
                onClose={() => setDocumentToDelete(null)}
                onSuccess={() => {
                    refreshDirectory();
                    setDocumentToDelete(null);
                }}
            />
            <RenameModal
                open={documentToRename !== null}
                document={documentToRename}
                onClose={() => setDocumentToRename(null)}
                onSuccess={() => {
                    refreshDirectory();
                    setDocumentToRename(null);
                }}
            />
            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity as any}
                handleCloseSnackbar={handleCloseSnackbar}
            />
        </Box>
    );
};

export default MissionDocuments;
